import { useEffect, useState } from 'react'
import { Loader2, Blocks } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  useRequestWidgetEmbarque,
  useUpdateWidgetEmbarque,
  useWidgetEmbarque,
} from '@/api/integrations'

/**
 * Widget marque blanche : la voix et les textos de Callizy dans le logiciel du
 * partenaire, sans rien de nous à l'écran. L'admin demande ici ; l'ouverture
 * se fait chez Callizy. Une fois actif, les trois réglages et les domaines.
 */
export function WidgetEmbarqueTab() {
  const { t } = useTranslation()
  const { data, isLoading } = useWidgetEmbarque()
  const demander = useRequestWidgetEmbarque()
  const modifier = useUpdateWidgetEmbarque()

  const [plafond, setPlafond] = useState('')
  const [origines, setOrigines] = useState('')

  useEffect(() => {
    if (!data) return
    setPlafond(data.seat_limit == null ? '' : String(data.seat_limit))
    setOrigines(data.allowed_origins.join('\n'))
  }, [data])

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    )
  }

  const carte = 'bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-200 dark:border-slate-700 p-6'

  const enregistrer = async (patch: Parameters<typeof modifier.mutateAsync>[0]) => {
    try {
      await modifier.mutateAsync(patch)
      toast.success(t('manager.widgetEmbarque.saved'))
    } catch (e: unknown) {
      // Le serveur explique pourquoi un domaine est refusé : on le dit, mot pour mot.
      const d = (e as { response?: { data?: { message?: string; origin?: string } } })?.response?.data
      if (d?.message) toast.error(d.origin ? `${d.origin} : ${d.message}` : d.message)
      else toast.error(t('manager.widgetEmbarque.saveError'))
    }
  }

  const enregistrerOrigines = () => {
    const liste = origines
      .split(/[\n,\s]+/)
      .map((o) => o.trim())
      .filter(Boolean)
    void enregistrer({ allowed_origins: liste })
  }

  const enregistrerPlafond = () => {
    const v = plafond.trim()
    void enregistrer({ seat_limit: v === '' ? null : Number(v) })
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className={carte}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
            <Blocks className="w-5 h-5 text-slate-700 dark:text-slate-200" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('manager.widgetEmbarque.title')}
            </h2>
            {data.status !== 'actif' ? (
              <>
                <p className="text-base text-slate-800 dark:text-slate-100 mt-1 font-medium">
                  {t('manager.widgetEmbarque.description')}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {t('manager.widgetEmbarque.subtitle')}
                </p>
                <div className="grid gap-4 sm:grid-cols-3 mt-5">
                  {(['point1', 'point2', 'point3'] as const).map((k) => (
                    <div key={k}>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{t(`manager.widgetEmbarque.${k}Title`)}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t(`manager.widgetEmbarque.${k}`)}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t('manager.widgetEmbarque.activeSubtitle')}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Etat status={data.status} />
            <a
              href="https://callizy.ca/docs#description/widget-white-label"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              {t('manager.widgetEmbarque.guide')}
            </a>
          </div>
          {data.status === 'off' && (
            <Button onClick={() => demander.mutate()} disabled={demander.isPending}>
              {demander.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {t('manager.widgetEmbarque.request')}
            </Button>
          )}
        </div>
        {data.status === 'demande' && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
            {t('manager.widgetEmbarque.pending')}
          </p>
        )}
      </div>

      {data.status === 'actif' && (
        <>
          <div className={carte}>
            <h3 className="font-semibold text-slate-900 dark:text-white">{t('manager.widgetEmbarque.settingsTitle')}</h3>
            <div className="mt-4 space-y-5">
              <Reglage
                titre={t('manager.widgetEmbarque.phoneRequired')}
                aide={t('manager.widgetEmbarque.phoneRequiredHelp')}
                actif={data.phone_verification_required}
                onChange={(v) => void enregistrer({ phone_verification_required: v })}
              />
              <Reglage
                titre={t('manager.widgetEmbarque.autoCreate')}
                aide={t('manager.widgetEmbarque.autoCreateHelp')}
                actif={data.auto_create_users}
                onChange={(v) => void enregistrer({ auto_create_users: v })}
              />
              <Reglage
                titre={t('manager.widgetEmbarque.forwardToMobile')}
                aide={t('manager.widgetEmbarque.forwardToMobileHelp')}
                actif={data.forward_to_mobile}
                onChange={(v) => void enregistrer({ forward_to_mobile: v })}
              />
              <div>
                <Label className="text-sm font-medium text-slate-900 dark:text-white">
                  {t('manager.widgetEmbarque.seatLimit')}
                </Label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  {t('manager.widgetEmbarque.seatLimitHelp', { count: data.linked_users })}
                </p>
                <div className="flex gap-2 max-w-xs">
                  <Input
                    inputMode="numeric"
                    value={plafond}
                    onChange={(e) => setPlafond(e.target.value.replace(/\D/g, ''))}
                    placeholder={t('manager.widgetEmbarque.noLimit')}
                  />
                  <Button variant="outline" onClick={enregistrerPlafond} disabled={modifier.isPending}>
                    {t('manager.widgetEmbarque.save')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className={carte}>
            <h3 className="font-semibold text-slate-900 dark:text-white">{t('manager.widgetEmbarque.originsTitle')}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 mb-3">
              {t('manager.widgetEmbarque.originsHelp')}
            </p>
            <Textarea
              value={origines}
              onChange={(e) => setOrigines(e.target.value)}
              rows={3}
              placeholder={'https://app.exemple.com'}
              className="font-mono text-sm"
            />
            <div className="mt-3">
              <Button variant="outline" onClick={enregistrerOrigines} disabled={modifier.isPending}>
                {t('manager.widgetEmbarque.save')}
              </Button>
            </div>
          </div>

        </>
      )}
    </div>
  )
}

function Etat({ status }: { status: 'off' | 'demande' | 'actif' }) {
  const { t } = useTranslation()
  const styles = {
    off: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    demande: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
    actif: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
  }[status]
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${styles}`}>
      {t(`manager.widgetEmbarque.status.${status}`)}
    </span>
  )
}

function Reglage({
  titre,
  aide,
  actif,
  onChange,
}: {
  titre: string
  aide: string
  actif: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-slate-900 dark:text-white">{titre}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{aide}</p>
      </div>
      <Switch checked={actif} onCheckedChange={onChange} />
    </div>
  )
}
